/**
 * Whole-document visual diff (Codex, ADR-006). Compares two `CanonicalDocument`
 * snapshots (a revision vs the live doc, or any two revisions) into a structured
 * model the History "Compare" view renders. Pure and fully tested; the UI stays
 * dumb. Companion to the coarse one-line `summarizeRevisionChange` — this is the
 * rich, on-demand view-time diff.
 *
 * Load-bearing facts (schema.ts): node `id`s are permanent and never reused, so
 * matching by id is exact (no fuzzy alignment). `orderIndex` is REAL/fractional,
 * so a re-index that does NOT change reading order is noise — we detect "moved"
 * from reading-order position + parent, never from raw `orderIndex`.
 */

import type { CanonicalDocument, Commentary, DocNode, InlineLine } from './schema';
import { commentaryChanged } from './revision-diff';
import { layoutNodes } from './layout';
import { diffWords, type WordOp } from './word-diff';

export type NodeDiffStatus = 'added' | 'removed' | 'edited' | 'moved' | 'unchanged';

export interface NodeDiff {
  id: string;
  status: NodeDiffStatus;
  /** Render source for 'removed'. */
  before?: DocNode;
  /** Render source for every other status. */
  after?: DocNode;
  /** Word ops per line; present when text changed ('edited', or 'moved' + edited). */
  lineDiffs?: WordOp[][];
  /** Present when 'moved': how the position changed (for the badge/caption). */
  move?: { reParented: boolean; reordered: boolean; fromParentId: string | null };
}

export interface CommentaryDiff {
  id: string;
  status: 'added' | 'removed' | 'edited' | 'unchanged';
  before?: Commentary;
  after?: Commentary;
  /** Which meta fields differ (for chips); body changes don't appear here. */
  metaChanged?: Array<'kind' | 'audience' | 'target' | 'anchors'>;
}

export interface DocumentDiff {
  /** Blocks in AFTER reading order, with removed blocks spliced at their old position. */
  nodes: NodeDiff[];
  commentary: CommentaryDiff[];
  documentMeta: {
    titleChanged: boolean;
    subtitleChanged: boolean;
    notesChanged: boolean;
    prevTitle: string;
    prevSubtitle: string | null;
  };
  counts: {
    nodesAdded: number;
    nodesRemoved: number;
    nodesEdited: number;
    nodesMoved: number;
    notesAdded: number;
    notesRemoved: number;
    notesEdited: number;
  };
}

const lineText = (line: InlineLine): string => line.runs.map((r) => r.text).join('');
const nodeText = (node: DocNode): string => node.lines.map(lineText).join('\n');

/** Text/type/label change only — the "edited" axis, deliberately excluding the
 *  parent/order "moved" axis so the two are reported distinctly. */
function contentChanged(a: DocNode, b: DocNode): boolean {
  return (
    a.type !== b.type || a.label !== b.label || JSON.stringify(a.lines) !== JSON.stringify(b.lines)
  );
}

/** Per-line word diff when line counts match; else one combined whole-block diff. */
function lineDiffsFor(before: DocNode, after: DocNode): WordOp[][] {
  if (before.lines.length === after.lines.length) {
    const afterText = after.lines.map(lineText);
    return before.lines.map((line, i) => diffWords(lineText(line), afterText[i] ?? ''));
  }
  return [diffWords(nodeText(before), nodeText(after))];
}

/** Ids of `a` that lie on a longest common subsequence with `b` (both are unique
 *  id sequences) — i.e. the ids whose RELATIVE reading order is preserved. Ids not
 *  returned are the ones that moved. */
function stableOrderIds(a: string[], b: string[]): Set<string> {
  const m = a.length;
  const n = b.length;
  // `?? 0` / `?? ''` keep this lint-clean under noUncheckedIndexedAccess; every
  // index is in bounds (ids are never the empty string) so fallbacks never fire.
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    const row = dp[i] ?? [];
    const next = dp[i + 1] ?? [];
    for (let j = n - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? (next[j + 1] ?? 0) + 1 : Math.max(next[j] ?? 0, row[j + 1] ?? 0);
    }
  }
  const stable = new Set<string>();
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    const ai = a[i] ?? '';
    if (ai === (b[j] ?? '')) {
      stable.add(ai);
      i++;
      j++;
    } else if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) i++;
    else j++;
  }
  return stable;
}

function metaChangedFields(a: Commentary, b: Commentary): CommentaryDiff['metaChanged'] {
  const fields: NonNullable<CommentaryDiff['metaChanged']> = [];
  if (a.kind !== b.kind) fields.push('kind');
  if (a.audience !== b.audience) fields.push('audience');
  if (a.targetNodeId !== b.targetNodeId) fields.push('target');
  if (JSON.stringify(a.extraTargets) !== JSON.stringify(b.extraTargets)) fields.push('anchors');
  return fields.length ? fields : undefined;
}

/**
 * Diff `after` relative to `before`. Both are full (commission-tier) snapshots —
 * keep it that way: a base/after pair at different audience tiers would read every
 * filtered-out note as a spurious add/remove. The Compare feature is Commission-only.
 */
export function diffDocuments(before: CanonicalDocument, after: CanonicalDocument): DocumentDiff {
  const beforeOrdered = layoutNodes(before.nodes).ordered;
  const afterOrdered = layoutNodes(after.nodes).ordered;
  const beforeById = new Map(beforeOrdered.map((n) => [n.id, n]));
  const afterById = new Map(afterOrdered.map((n) => [n.id, n]));

  // Reading-order position is "stable" only among nodes present on BOTH sides, so
  // an added/removed neighbour never makes an untouched block look moved.
  const commonInBefore = beforeOrdered.filter((n) => afterById.has(n.id)).map((n) => n.id);
  const commonInAfter = afterOrdered.filter((n) => beforeById.has(n.id)).map((n) => n.id);
  const stable = stableOrderIds(commonInBefore, commonInAfter);

  const counts = {
    nodesAdded: 0,
    nodesRemoved: 0,
    nodesEdited: 0,
    nodesMoved: 0,
    notesAdded: 0,
    notesRemoved: 0,
    notesEdited: 0,
  };

  // Build the after-order list first.
  const nodes: NodeDiff[] = afterOrdered.map((after_) => {
    const before_ = beforeById.get(after_.id);
    if (!before_) {
      counts.nodesAdded++;
      return { id: after_.id, status: 'added', after: after_ };
    }
    const reParented = before_.parentId !== after_.parentId;
    const reordered = !stable.has(after_.id);
    const edited = contentChanged(before_, after_);
    if (reParented || reordered) {
      counts.nodesMoved++;
      return {
        id: after_.id,
        status: 'moved',
        before: before_,
        after: after_,
        move: { reParented, reordered, fromParentId: before_.parentId },
        ...(edited ? { lineDiffs: lineDiffsFor(before_, after_) } : {}),
      };
    }
    if (edited) {
      counts.nodesEdited++;
      return {
        id: after_.id,
        status: 'edited',
        before: before_,
        after: after_,
        lineDiffs: lineDiffsFor(before_, after_),
      };
    }
    return { id: after_.id, status: 'unchanged', before: before_, after: after_ };
  });

  // Splice removed blocks in, each right after its nearest surviving predecessor.
  let insertAfterId: string | null = null;
  for (const before_ of beforeOrdered) {
    if (afterById.has(before_.id)) {
      insertAfterId = before_.id;
      continue;
    }
    counts.nodesRemoved++;
    const entry: NodeDiff = { id: before_.id, status: 'removed', before: before_ };
    const pos = insertAfterId ? nodes.findIndex((e) => e.id === insertAfterId) : -1;
    nodes.splice(pos + 1, 0, entry);
    insertAfterId = before_.id; // keep consecutive removed blocks in order
  }

  // Commentary — whole-note status (no intra-body word diff). Live notes only.
  const beforeNotes = before.commentary.filter((c) => !c.deleted);
  const afterNotes = after.commentary.filter((c) => !c.deleted);
  const beforeNoteById = new Map(beforeNotes.map((c) => [c.id, c]));
  const afterNoteById = new Map(afterNotes.map((c) => [c.id, c]));

  const commentary: CommentaryDiff[] = afterNotes.map((after_) => {
    const before_ = beforeNoteById.get(after_.id);
    if (!before_) {
      counts.notesAdded++;
      return { id: after_.id, status: 'added', after: after_ };
    }
    if (commentaryChanged(before_, after_)) {
      counts.notesEdited++;
      const metaChanged = metaChangedFields(before_, after_);
      return {
        id: after_.id,
        status: 'edited',
        before: before_,
        after: after_,
        // Omit (not set to undefined) when no meta field changed — exactOptionalPropertyTypes.
        ...(metaChanged ? { metaChanged } : {}),
      };
    }
    return { id: after_.id, status: 'unchanged', before: before_, after: after_ };
  });
  for (const before_ of beforeNotes) {
    if (afterNoteById.has(before_.id)) continue;
    counts.notesRemoved++;
    commentary.push({ id: before_.id, status: 'removed', before: before_ });
  }

  return {
    nodes,
    commentary,
    documentMeta: {
      titleChanged: before.document.title !== after.document.title,
      subtitleChanged: before.document.subtitle !== after.document.subtitle,
      notesChanged: JSON.stringify(before.document.notes) !== JSON.stringify(after.document.notes),
      prevTitle: before.document.title,
      prevSubtitle: before.document.subtitle,
    },
    counts,
  };
}
